import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://dailydish.in/api/admin/menu-intelligence";

const SESSION_LABEL = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
};

const MEAL_COLOR = {
  breakfast: { bg: "#FFF8DC", border: "#E6B800", badge: "#8B4513" },
  lunch: { bg: "#F0FFF0", border: "#6B8E23", badge: "#2d5a00" },
  dinner: { bg: "#F5F0FF", border: "#7B68EE", badge: "#3d2a8a" },
};

// ── Small components ─────────────────────────────────────────────────────────

function RepeatBadge() {
  return (
    <span
      style={{
        background: "#FFF3CD",
        color: "#856404",
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 7px",
        borderRadius: 20,
        border: "1px solid #ffc107",
        whiteSpace: "nowrap",
      }}
    >
      ⚠ Repeat
    </span>
  );
}

function EmptySlot({ label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        background: "#fff",
        borderRadius: 8,
        border: "1.5px dashed #ddd",
        marginBottom: 6,
      }}
    >
      <span style={{ fontSize: 12, color: "#aaa" }}>{label}</span>
      <span style={{ fontSize: 11, color: "#cc4125", fontWeight: 600 }}>
        No candidates found
      </span>
    </div>
  );
}

function SlotRow({ slotData, onOverride }) {
  const { slot, label, item, candidates } = slotData;
  const [showDropdown, setShowDropdown] = useState(false);

  if (!item) return <EmptySlot label={label} />;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        background: "#fff",
        borderRadius: 8,
        border: "1.5px solid #eee",
        marginBottom: 6,
      }}
    >
      {/* Slot label */}
      <span
        style={{
          fontSize: 11,
          color: "#888",
          minWidth: 110,
          fontWeight: 500,
        }}
      >
        {label}
      </span>

      {/* Dish name + repeat badge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: "#2C2C2C" }}>
            {item.foodname}
          </span>
          {item.isRecentRepeat && <RepeatBadge />}
        </div>
        <span style={{ fontSize: 11, color: "#999" }}>
          ₹{item.foodprice} · {item.categoryName}
        </span>
      </div>

      {/* Override button */}
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setShowDropdown((v) => !v)}
          style={{
            fontSize: 11,
            padding: "4px 12px",
            borderRadius: 20,
            border: "1px solid #ccc",
            background: "#f9f9f9",
            cursor: "pointer",
            color: "#555",
            fontWeight: 600,
          }}
        >
          Change ▾
        </button>

        {showDropdown && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 4px)",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 10,
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              zIndex: 100,
              minWidth: 240,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {candidates.map((c) => (
              <div
                key={c._id}
                onClick={() => {
                  onOverride(slot, c);
                  setShowDropdown(false);
                }}
                style={{
                  padding: "9px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  borderBottom: "1px solid #f5f5f5",
                  background: item._id === c._id ? "#FFF8DC" : "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => {
                  if (item._id !== c._id)
                    e.currentTarget.style.background = "#fafafa";
                }}
                onMouseLeave={(e) => {
                  if (item._id !== c._id)
                    e.currentTarget.style.background = "#fff";
                }}
              >
                <span
                  style={{
                    fontWeight: item._id === c._id ? 700 : 400,
                    color: "#2C2C2C",
                  }}
                >
                  {c.foodname}
                  {item._id === c._id && " ✓"}
                </span>
                <span style={{ fontSize: 11, color: "#888" }}>
                  ₹{c.foodprice}
                </span>
              </div>
            ))}
            {candidates.length === 0 && (
              <div
                style={{ padding: "12px 14px", color: "#aaa", fontSize: 12 }}
              >
                No options
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MealSection({
  title,
  session,
  slots,
  color,
  onOverride,
  onPublish,
  publishing,
}) {
  const filled = slots.filter((s) => s.item).length;
  const total = slots.length;
  const allOk = filled === total;

  return (
    <div
      style={{
        background: color.bg,
        borderRadius: 14,
        border: `1.5px solid ${color.border}`,
        padding: "16px 18px",
        marginBottom: 20,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              background: color.border,
              color: "#fff",
              borderRadius: 20,
              padding: "3px 12px",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: allOk ? "#2d6a2d" : "#cc4125",
            }}
          >
            {filled}/{total} slots filled
          </span>
        </div>
        <button
          onClick={() => onPublish(session)}
          disabled={publishing === session || filled === 0}
          style={{
            background: filled === 0 ? "#ccc" : color.border,
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "6px 18px",
            fontSize: 12,
            fontWeight: 700,
            cursor: filled === 0 ? "not-allowed" : "pointer",
            opacity: publishing === session ? 0.7 : 1,
          }}
        >
          {publishing === session ? "Publishing…" : `Publish ${title}`}
        </button>
      </div>

      {/* Slots */}
      {slots.map((s) => (
        <SlotRow key={s.slot} slotData={s} onOverride={onOverride} />
      ))}
    </div>
  );
}

function ComboSection({ combos }) {
  if (!combos || combos.length === 0) return null;
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: "1.5px solid #E6B800",
        padding: "16px 18px",
        marginBottom: 20,
      }}
    >
      <span
        style={{
          background: "#E6B800",
          color: "#5A3000",
          borderRadius: 20,
          padding: "3px 12px",
          fontSize: 12,
          fontWeight: 700,
          display: "inline-block",
          marginBottom: 14,
        }}
      >
        Combos ({combos.length})
      </span>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {combos.map((c) => (
          <div
            key={c.comboNumber}
            style={{
              border: "1.5px solid #eee",
              borderRadius: 10,
              padding: "10px 14px",
              background: "#FAFAFA",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#2C2C2C",
                marginBottom: 4,
              }}
            >
              {c.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#666",
                marginBottom: 8,
                lineHeight: 1.6,
              }}
            >
              {c.items.map((i) => i.foodname).join(" · ")}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#6B8E23" }}>
                ₹{c.comboPrice}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: "#aaa",
                  textDecoration: "line-through",
                }}
              >
                ₹{c.originalPrice}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: "#cc4125",
                  borderRadius: 10,
                  padding: "2px 6px",
                }}
              >
                15% off
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function MenuIntelligence() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(null);
  const [publishing, setPublishing] = useState(null); // session being published

  // Local overrides: { [slot]: item }
  const [overrides, setOverrides] = useState({});

  const handleGenerate = async () => {
    setLoading(true);
    setMenu(null);
    setOverrides({});
    try {
      const res = await axios.get(`${API}/generate`, { params: { date } });
      setMenu(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to generate menu");
    } finally {
      setLoading(false);
    }
  };

  const handleOverride = (slot, newItem) => {
    setOverrides((prev) => ({ ...prev, [slot]: newItem }));
  };

  // Merge overrides into slots
  const applyOverrides = (slots) =>
    slots.map((s) => ({
      ...s,
      item: overrides[s.slot] !== undefined ? overrides[s.slot] : s.item,
    }));

  const handlePublish = async (session) => {
    if (!menu) return;
    const rawSlots = menu[session] || [];
    const slots = applyOverrides(rawSlots);
    const items = slots.filter((s) => s.item).map((s) => s.item);
    if (items.length === 0) {
      toast.warn("No items to publish");
      return;
    }

    setPublishing(session);
    try {
      const res = await axios.post(`${API}/publish`, { date, session, items });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || "Publish failed");
    } finally {
      setPublishing(null);
    }
  };

  const stats = menu?.stats;

  return (
    <div
      style={{
        padding: "24px 20px",
        maxWidth: 900,
        margin: "0 auto",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: "#2C2C2C",
            margin: 0,
            letterSpacing: -0.5,
          }}
        >
          Menu Intelligence
        </h2>
        <p
          style={{ fontSize: 13, color: "#888", marginTop: 4, marginBottom: 0 }}
        >
          Auto-generate today's menu based on sales popularity and 14-day repeat
          rules, then publish to all hubs.
        </p>
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 28,
          background: "#FFF8DC",
          borderRadius: 12,
          padding: "14px 18px",
          border: "1.5px solid #E6B800",
        }}
      >
        <label
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#8B4513",
            whiteSpace: "nowrap",
          }}
        >
          Menu Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{
            padding: "7px 12px",
            borderRadius: 8,
            border: "1.5px solid #E6B800",
            fontSize: 13,
            fontWeight: 600,
            color: "#2C2C2C",
            background: "#fff",
            cursor: "pointer",
            outline: "none",
          }}
        />
        <button
          onClick={handleGenerate}
          disabled={loading}
          style={{
            background: "#6B8E23",
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "8px 24px",
            fontSize: 13,
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
            letterSpacing: -0.2,
          }}
        >
          {loading ? "Generating…" : "Generate Menu"}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div
          style={{
            textAlign: "center",
            padding: "40px 0",
            color: "#888",
            fontSize: 14,
          }}
        >
          Analysing products and applying rules…
        </div>
      )}

      {/* Stats bar */}
      {menu && stats && (
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          {["breakfast", "lunch", "dinner"].map((s) => (
            <div
              key={s}
              style={{
                background: "#fff",
                border: `1.5px solid ${MEAL_COLOR[s].border}`,
                borderRadius: 10,
                padding: "8px 16px",
                fontSize: 12,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  color: MEAL_COLOR[s].badge,
                  textTransform: "capitalize",
                }}
              >
                {s}
              </span>{" "}
              <span
                style={{
                  fontWeight: 700,
                  color:
                    stats[s].filled === stats[s].total ? "#2d6a2d" : "#cc4125",
                }}
              >
                {stats[s].filled}/{stats[s].total}
              </span>
            </div>
          ))}
          <div
            style={{
              background: "#fff",
              border: "1.5px solid #E6B800",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 12,
            }}
          >
            <span style={{ fontWeight: 700, color: "#8B4513" }}>Combos </span>
            <span style={{ fontWeight: 700, color: "#2C2C2C" }}>
              {stats.combos.built}
            </span>
          </div>
          <div
            style={{
              background: "#fff",
              border: "1.5px solid #ddd",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 12,
              color: "#888",
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {stats.recentlyBlockedCount}
            </span>{" "}
            dishes blocked (14-day rule)
          </div>
        </div>
      )}

      {/* Menu sections */}
      {menu && (
        <>
          <MealSection
            title="Breakfast"
            session="breakfast"
            slots={applyOverrides(menu.breakfast)}
            color={MEAL_COLOR.breakfast}
            onOverride={handleOverride}
            onPublish={handlePublish}
            publishing={publishing}
          />
          <MealSection
            title="Lunch"
            session="lunch"
            slots={applyOverrides(menu.lunch)}
            color={MEAL_COLOR.lunch}
            onOverride={handleOverride}
            onPublish={handlePublish}
            publishing={publishing}
          />
          <ComboSection combos={menu.combos} />
          <MealSection
            title="Dinner"
            session="dinner"
            slots={applyOverrides(menu.dinner)}
            color={MEAL_COLOR.dinner}
            onOverride={handleOverride}
            onPublish={handlePublish}
            publishing={publishing}
          />

          {/* Publish all */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 8,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <button
              onClick={async () => {
                for (const s of ["breakfast", "lunch", "dinner"])
                  await handlePublish(s);
              }}
              disabled={!!publishing}
              style={{
                background: "#2C2C2C",
                color: "#fff",
                border: "none",
                borderRadius: 20,
                padding: "10px 28px",
                fontSize: 13,
                fontWeight: 700,
                cursor: publishing ? "not-allowed" : "pointer",
                opacity: publishing ? 0.6 : 1,
              }}
            >
              {publishing ? "Publishing…" : "Publish All Sessions"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
